import * as React from "react";
import { useState, useEffect } from "react";
import { Button, Input } from "@/components/ui";
import { MfaServices } from "@/services/bedrock/MfaServices";
import { useSocialCoachSession } from "@/utils";
import { base64ToBuffer, bufferToBase64 } from "@/utils/mfaHelpers";
import { ShieldCheck, Loader2, ArrowRight, Mail, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { Svg } from "@/assets";
import { useSmsSetup } from "../context";

export const VerifyIdentity: React.FC = () => {
  const { setStep, isLoading, setIsLoading } = useSmsSetup();
  const { session, refreshSession, isAdmin } = useSocialCoachSession();

  const [identityStep, setIdentityStep] = useState<"SELECT_METHOD" | "ENTER_CODE">("SELECT_METHOD");
  const [selectedIdentityMethod, setSelectedIdentityMethod] = useState<"email" | "slack" | "passkey" | null>(null);
  const [identityCode, setIdentityCode] = useState("");
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);

  useEffect(() => {
    // Check for passkeys on mount
    MfaServices.getAuthChallenge()
      .then(options => setHasPasskey(!!options.allowCredentials?.length))
      .catch(() => setHasPasskey(false));
  }, []);

  const handleStartVerification = async (method: "email" | "slack") => {
    setIsLoading(true);
    setSelectedIdentityMethod(method);
    try {
      await MfaServices.requestChallenge(method);
      setIdentityStep("ENTER_CODE");
      setIdentityCode("");
      toast.success(`Security code sent to your ${method === "slack" ? "Slack account" : "email"}!`);
    } catch (e) {
      toast.error(`Failed to send ${method === "slack" ? "Slack" : "email"} verification code`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartVerificationPasskey = async () => {
    setIsLoading(true);
    setSelectedIdentityMethod("passkey");
    try {
      const options = await MfaServices.getAuthChallenge();

      const credential = (await navigator.credentials.get({
        publicKey: {
          challenge: base64ToBuffer(options.challenge),
          rpId: options.rpId,
          allowCredentials: options.allowCredentials?.map(c => ({
            ...c,
            id: base64ToBuffer(c.id),
          })),
          userVerification: options.userVerification,
          timeout: options.timeout,
        },
      })) as PublicKeyCredential;

      if (!credential) throw new Error("Passkey authentication failed");

      const response = credential.response as AuthenticatorAssertionResponse;
      const payload = {
        id: credential.id,
        rawId: bufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: bufferToBase64(response.authenticatorData),
          clientDataJSON: bufferToBase64(response.clientDataJSON),
          signature: bufferToBase64(response.signature),
          userHandle: response.userHandle ? bufferToBase64(response.userHandle) : null,
        },
        origin: window.location.origin,
      };

      const result = await MfaServices.verifyAuth(payload);
      if (result.status === "success") {
        toast.success("Identity verified successfully!");
        await refreshSession(true);
        setStep("ENTER_PHONE");
      } else {
        toast.error("Passkey verification failed");
      }
    } catch (e) {
      toast.error("Failed to verify identity with passkey");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyIdentityEmailCode = async () => {
    if (identityCode.length < 6 || !selectedIdentityMethod) return;
    setIsLoading(true);
    try {
      const result = await MfaServices.verifyChallenge(identityCode, selectedIdentityMethod as "email" | "slack");
      if (result.status === "success") {
        toast.success("Identity verified successfully!");
        await refreshSession(true);
        setStep("ENTER_PHONE");
      } else {
        toast.error("Invalid verification code");
      }
    } catch (e) {
      toast.error("Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleIdentityPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const isAlphanumeric = selectedIdentityMethod === "slack";
    const cleanRegex = isAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
    const pastedData = e.clipboardData.getData("text").replace(cleanRegex, "").slice(0, 6).toUpperCase();
    if (pastedData) {
      setIdentityCode(pastedData);
      const focusIndex = Math.min(pastedData.length, 5);
      document.getElementById(`identity-code-${focusIndex}`)?.focus();
    }
  };

  const renderVerifyIdentitySelect = () => {
    const showCards = hasPasskey || isAdmin;

    return (
      <div className="flex flex-col items-center justify-center py-6 space-y-6 flex-1 h-full max-w-lg mx-auto text-center animate-in fade-in duration-200">
        <div className="relative flex items-center justify-center">
          {/* Animated pulsing background */}
          <div className="absolute h-20 w-20 rounded-full bg-blue-50 animate-pulse duration-1000" />
          <div className="relative h-12 w-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-200">
            <ShieldCheck className="h-6 w-6 stroke-[2.5]" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-xl font-bold text-slate-800">Let's verify it's you</div>
          <p className="text-sm text-slate-500 leading-relaxed">
            Because you're accessing your sensitive security settings, please confirm your identity using one of your
            active verification methods below.
          </p>
        </div>

        {showCards ? (
          <div className="grid gap-3 w-full pt-2">
            {/* Email Option Card */}
            <button
              type="button"
              className="w-full bg-slate-50 border border-slate-200 hover:border-blue-300 hover:bg-blue-50/20 rounded-xl p-4 transition-all text-left flex items-center gap-4 group"
              onClick={() => handleStartVerification("email")}
              disabled={isLoading}
            >
              <div className="h-10 w-10 rounded-lg bg-blue-100/50 text-blue-600 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                <Mail className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-800">Email Verification Code</div>
                <div className="text-xs text-slate-400 truncate">Send secure code to {session?.email}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 transition-colors shrink-0" />
            </button>

            {/* Passkey Option Card */}
            {hasPasskey && (
              <button
                type="button"
                className="w-full bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/20 rounded-xl p-4 transition-all text-left flex items-center gap-4 group"
                onClick={handleStartVerificationPasskey}
                disabled={isLoading}
              >
                <div className="h-10 w-10 rounded-lg bg-indigo-100/50 text-indigo-600 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 transition-colors">
                  <Fingerprint className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">Use Passkey or Biometrics</div>
                  <div className="text-xs text-slate-400">Unlock with Face ID, Fingerprint, or device PIN</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0" />
              </button>
            )}

            {/* Slack Option Card */}
            {isAdmin && (
              <button
                type="button"
                className="w-full bg-slate-50 border border-slate-200 hover:border-purple-300 hover:bg-purple-50/20 rounded-xl p-4 transition-all text-left flex items-center gap-4 group"
                onClick={() => handleStartVerification("slack")}
                disabled={isLoading}
              >
                <div className="h-10 w-10 rounded-lg bg-purple-100/50 text-purple-600 flex items-center justify-center shrink-0 group-hover:bg-purple-100 transition-colors p-2">
                  <img src={Svg.SlackSVG} className="h-full w-full object-contain" alt="Slack Logo" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800">Slack Notification Code</div>
                  <div className="text-xs text-slate-400">Send secure code to your connected Slack account</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-purple-500 transition-colors shrink-0" />
              </button>
            )}
          </div>
        ) : (
          <div className="w-full pt-4 max-w-xs space-y-4">
            <Button
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl gap-2 hover:translate-y-[-1px] active:translate-y-[1px] transition-all shadow-md shadow-primary/20"
              onClick={() => handleStartVerification("email")}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Mail className="h-4.5 w-4.5" />
                  <span>Verify via Email Code</span>
                </>
              )}
            </Button>
            <p className="text-[11px] text-slate-400">
              We'll send a 6-digit code to <span className="font-semibold">{session?.email}</span>.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderVerifyIdentityCode = () => (
    <div className="flex flex-col items-center justify-center py-6 space-y-6 flex-1 h-full max-w-md mx-auto text-center animate-in fade-in duration-200">
      <div className="w-full space-y-6">
        <div className="space-y-2">
          <div className="text-xl font-bold text-slate-800">Enter security code</div>
          <p className="text-sm text-slate-500 leading-relaxed font-medium">
            We've sent a 6-digit verification code to your{" "}
            <span className="font-bold text-slate-800">
              {selectedIdentityMethod === "slack" ? "Slack account" : `email (${session?.email})`}
            </span>
            .
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex justify-center gap-2.5">
            {[...Array(6)].map((_, i) => {
              const isAlphanumeric = selectedIdentityMethod === "slack";
              return (
                <Input
                  key={i}
                  id={`identity-code-${i}`}
                  className="w-12 h-14 text-center text-xl font-bold border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 shadow-sm rounded-xl p-0 uppercase"
                  value={identityCode[i] || ""}
                  onPaste={handleIdentityPaste}
                  onChange={e => {
                    const cleanRegex = isAlphanumeric ? /[^a-zA-Z0-9]/g : /\D/g;
                    const val = e.target.value.replace(cleanRegex, "").slice(-1).toUpperCase();
                    const newCode = identityCode.split("");
                    newCode[i] = val;
                    const joined = newCode.join("");
                    setIdentityCode(joined);
                    if (val && i < 5) {
                      document.getElementById(`identity-code-${i + 1}`)?.focus();
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === "Backspace" && !identityCode[i] && i > 0) {
                      document.getElementById(`identity-code-${i - 1}`)?.focus();
                    } else if (e.key === "Enter" && identityCode.length === 6 && !isLoading) {
                      e.preventDefault();
                      handleVerifyIdentityEmailCode();
                    }
                  }}
                  inputMode={isAlphanumeric ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  autoFocus={i === 0}
                />
              );
            })}
          </div>

          <div className="text-xs text-slate-400">
            Didn't receive a code?{" "}
            <button
              type="button"
              className="text-blue-600 hover:underline font-semibold"
              onClick={() => handleStartVerification(selectedIdentityMethod as "email" | "slack")}
              disabled={isLoading}
            >
              Resend Code
            </button>
          </div>
        </div>
      </div>

      <div className="w-full space-y-3">
        <Button
          className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-md gap-2 hover:translate-y-[-1px] active:translate-y-[1px] transition-all disabled:opacity-50"
          onClick={handleVerifyIdentityEmailCode}
          disabled={isLoading || identityCode.length < 6}
        >
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Verify Identity"}
        </Button>

        <button
          type="button"
          className="text-sm font-semibold text-slate-400 hover:text-slate-600 transition-colors block mx-auto py-1"
          onClick={() => {
            setIdentityStep("SELECT_METHOD");
            setIdentityCode("");
          }}
          disabled={isLoading}
        >
          Go Back
        </button>
      </div>
    </div>
  );

  return identityStep === "SELECT_METHOD" ? renderVerifyIdentitySelect() : renderVerifyIdentityCode();
};
